import clsx from 'clsx'
import { BreakdownCounts, BreakdownSegment, breakdownSegments } from './breakdownSegments'

// Written out in full so Tailwind's purge keeps them — the moderation palette
// is the same one SummaryStatistics and StatusPill draw from.
const BAR_COLOURS: Record<BreakdownSegment['id'], string> = {
    severe: 'bg-severe',
    suspect: 'bg-suspect',
    safe: 'bg-safe',
    unmoderated: 'bg-ink-200'
}
const TEXT_COLOURS: Record<BreakdownSegment['id'], string> = {
    severe: 'text-severe',
    suspect: 'text-suspect',
    safe: 'text-safe',
    unmoderated: 'text-ink-500'
}

type Props = BreakdownCounts & {
    /** `row` is the compact table cell; `detail` adds labels for the post page. */
    variant?: 'row' | 'detail'
}

/**
 * A stacked bar of a post's comments by moderation outcome, with the counts
 * beside it. Severe leads so the figure that matters most is always in the
 * same place as the eye scans down a column.
 */
export default function ModerationBreakdown({ variant = 'row', ...counts }: Props) {
    const segments = breakdownSegments(counts)
    const summary = segments.map((s) => `${s.count} ${s.label.toLowerCase()}`).join(', ')

    return (
        <div className={clsx('flex flex-col', variant === 'row' ? 'gap-1' : 'gap-2')}>
            <div
                role="img"
                aria-label={summary}
                className={clsx(
                    'flex w-full overflow-hidden rounded-full bg-ink-100',
                    variant === 'row' ? 'h-1.5' : 'h-2.5'
                )}
            >
                {segments
                    .filter((s) => s.percent > 0)
                    .map((s) => (
                        <div
                            key={s.id}
                            className={BAR_COLOURS[s.id]}
                            style={{ width: `${s.percent}%` }}
                        />
                    ))}
            </div>
            <dl
                className={clsx(
                    'flex flex-wrap tabular-nums',
                    variant === 'row' ? 'gap-x-3 text-xs' : 'gap-x-5 text-sm'
                )}
            >
                {segments.map((s) => (
                    <div key={s.id} className="flex items-baseline gap-1">
                        <dd className={clsx('font-medium', TEXT_COLOURS[s.id])}>
                            {s.count.toLocaleString('en-GB')}
                        </dd>
                        <dt className={clsx(variant === 'row' && 'sr-only', 'text-ink-500')}>
                            {s.label}
                        </dt>
                    </div>
                ))}
            </dl>
        </div>
    )
}
